import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";
import { appConfig } from "@/config/app";

interface VirtualTableProps {
  headers: string[];
  rows: Record<string, unknown>[];
  totalRows?: number;
  maxHeight?: number | string;
  className?: string;
}

export function VirtualTable({ headers, rows, maxHeight = 400, className }: VirtualTableProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => appConfig.table.virtualRowHeight,
    overscan: appConfig.table.overscan,
  });

  const minTableWidth = headers.length * 140 + 50;

  return (
    <div
      className={cn(
        "rounded-lg border border-[var(--color-border-light)] overflow-hidden",
        className
      )}
    >
      <div ref={parentRef} className="overflow-auto" style={{ maxHeight }}>
        <div style={{ minWidth: `${minTableWidth}px` }}>
          {/* Header row */}
          <div className="flex bg-[var(--color-bg-secondary)] border-b border-[var(--color-border-light)]">
            <span className="px-3 py-2.5 text-xs font-semibold text-[var(--color-text-secondary)] w-12 shrink-0">
              #
            </span>
            {headers.map((h) => (
              <span
                key={h}
                className="px-3 py-2.5 text-xs font-semibold text-[var(--color-text-secondary)] truncate min-w-[120px] flex-1"
                title={h}
              >
                {h}
              </span>
            ))}
          </div>

          {/* Virtualized rows */}
          <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]!;
              return (
                <div
                  key={virtualRow.key}
                  className="flex border-b border-[var(--color-border-light)] hover:bg-[var(--color-bg-secondary)] transition-colors"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <span className="px-3 py-2 text-xs text-[var(--color-text-tertiary)] w-12 shrink-0">
                    {virtualRow.index}
                  </span>
                  {headers.map((h) => (
                    <span
                      key={h}
                      className="px-3 py-2 text-xs truncate min-w-[120px] flex-1"
                      title={String(row[h] ?? "")}
                    >
                      {String(row[h] ?? "")}
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
